/**
 * RiskAssessmentGapsPanel.jsx: BM-H643 (29 Sep 2026).
 *
 * Live matters with no risk assessment, on the Compliance Dashboard (MLCO and
 * COLP). Vanessa, 29 Sep 2026, "yes" to a count on the MLCO dashboard, with
 * each matter also on its fee earner's task list. Grouped by fee earner; each
 * matter links to its FORF. Server: GET /api/compliance/dashboard/risk-assessment-gaps.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

export default function RiskAssessmentGapsPanel({ authHeaders }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/compliance/dashboard/risk-assessment-gaps`, { headers: authHeaders ? authHeaders() : {} })
      .then((r) => setData(r.data))
      .catch((err) => setError(errorMessage(err, "Could not load the matters without a risk assessment")));
  }, [authHeaders]);

  const groups = useMemo(() => {
    const g = {};
    (data?.matters || []).forEach((m) => {
      const k = m.fee_earner || "No fee earner recorded";
      (g[k] = g[k] || []).push(m);
    });
    return Object.entries(g).sort((a, b) => b[1].length - a[1].length);
  }, [data]);

  if (error) {
    return (
      <section className="rounded-md border border-red-800/50 bg-red-950/30 p-4 text-sm text-red-200" data-testid="ra-gaps-panel">
        <span data-testid="ra-gaps-error">{error}</span>
      </section>
    );
  }
  if (!data) {
    return <section className="rounded-xl border border-white/[0.07] bg-navy-card p-4 text-xs text-slate-400" data-testid="ra-gaps-panel">Loading…</section>;
  }
  const bad = data.count > 0;
  return (
    <section className={`rounded-md border p-4 space-y-3 ${bad ? "border-amber-700/60 bg-amber-950/20" : "border-slate-700/60 bg-slate-900/50"}`}
      data-testid="ra-gaps-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left" aria-expanded={open}
        data-testid="ra-gaps-toggle">
        <h2 className="text-sm font-semibold text-slate-100">Live matters with no risk assessment</h2>
        <div className="text-xs text-slate-300 mt-1" data-testid="ra-gaps-count">
          {data.count} of {data.live_matters} live matters. {bad ? (open ? "Hide" : "Show by fee earner") : "None."}
        </div>
      </button>
      {open && bad && (
        <div className="space-y-3" data-testid="ra-gaps-list">
          {groups.map(([fe, ms]) => (
            <div key={fe} data-testid={`ra-gaps-fe-${fe}`}>
              <div className="text-xs font-semibold text-slate-200">{fe} ({ms.length})</div>
              <ul className="mt-1 space-y-0.5">
                {ms.map((m) => (
                  <li key={m.matter_ref} className="text-xs text-slate-300">
                    <a href={m.link} className="text-sky-300 underline" data-testid={`ra-gaps-link-${m.matter_ref}`}>{m.matter_ref}</a>
                    <span className="text-slate-500"> · {m.forf_state}{m.onboarding_status === "overridden" ? " · onboarding overridden" : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
